import React, { useState, useEffect, useRef } from 'react';
import './CreateGroupModal.css';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGroupCreated: (groupName: string, groupDescription: string) => void;
  organizationId: number;
  existingGroupNames?: string[];
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onGroupCreated,
  organizationId,
  existingGroupNames = []
}) => {
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const maxDescriptionLength = 500;
  const remainingChars = maxDescriptionLength - groupDescription.length;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setGroupName('');
      setGroupDescription('');
      setError(null);
      setIsSubmitting(false);
      // Focus on name input after a short delay
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, isSubmitting, onClose]);

  const validateForm = (): boolean => {
    setError(null);

    const trimmedName = groupName.trim();

    if (!trimmedName) {
      setError('Group name is required');
      return false;
    }

    if (trimmedName.length < 2) {
      setError('Group name must be at least 2 characters');
      return false;
    }

    if (trimmedName.length > 255) {
      setError('Group name must be less than 255 characters');
      return false;
    }

    // Check for duplicate names (case-insensitive)
    const isDuplicate = existingGroupNames.some(
      name => name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setError('A group with this name already exists');
      return false;
    }

    if (groupDescription.length > maxDescriptionLength) {
      setError(`Description must be less than ${maxDescriptionLength} characters`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onGroupCreated(groupName.trim(), groupDescription.trim());
      // Modal will be closed by parent component on success
    } catch (err) {
      console.error('Error creating group:', err);
      setError(err instanceof Error ? err.message : 'Failed to create group');
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      // Check if form has content
      if (groupName.trim() || groupDescription.trim()) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-group-modal-overlay" onClick={handleBackdropClick}>
      <div className="create-group-modal">
        <div className="modal-header">
          <h2>Create New Group</h2>
          <button
            className="close-button"
            onClick={onClose}
            disabled={isSubmitting}
            type="button"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠</span>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="groupName" className="form-label">
                Group Name <span className="required">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                id="groupName"
                className="form-input"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Northeast Region, Top Performers, Q1 Focus Group"
                maxLength={255}
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="groupDescription" className="form-label">
                Description <span className="optional">(Optional)</span>
              </label>
              <textarea
                id="groupDescription"
                className="form-textarea"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Describe the purpose or criteria for this group..."
                rows={4}
                maxLength={maxDescriptionLength}
                disabled={isSubmitting}
              />
              <div className="character-counter">
                {remainingChars} characters remaining
              </div>
            </div>

            <div className="info-box">
              <span className="info-icon">ℹ️</span>
              <span>You can assign locations to this group after creation by dragging them into the group area.</span>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={isSubmitting || !groupName.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Creating...
                </>
              ) : (
                'Create Group'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;

